import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, IconButton, Snackbar, Tooltip } from '@mui/material'
import axios from 'axios'
import {
  AgentChatProvider,
  AgentChat,
  useAgentChat,
  type AgentChatConfig,
  type ArtifactInfo,
  type ChatNoticeProps,
} from '../agent'
import useRouter from '../hooks/useRouter'
import { hasCompletedTurn } from '../hooks/useSkillsArea'
import useAccount from '../hooks/useAccount'
import useAiAccess from '../hooks/useAiAccess'
import useWhoToAsk from '../hooks/useWhoToAsk'
import useReleaseNotice from '../hooks/useReleaseNotice'
import { setTurnActive } from '../utils/turnActivity'
import { chatActionReason } from '../utils/chatActionReason'
import useSnackbarTopOffset from '../hooks/useSnackbarTopOffset'
import { snackbarPlacement } from '../contexts/snackbar'
import { usePageJob } from '../contexts/pageJob'
import useAgentModels from '../hooks/useAgentModels'
import { API_BASE_URL } from '../constants/system'
import { useChatHistoryActions } from '../hooks/useChatHistoryActions'
import { useOutputSearch, outputDestination } from '../hooks/useOutputSearch'
import { AgentSidePanel, EdgeReveal, SIDE_PANEL_HISTORY_KEY, SidePanelLauncher, useMicrophones, WebappFramesPausedContext, AgentCardHostContext, useAgentCardHost, PILL_RADIUS } from '../agent'
import { CHIEF_OF_STAFF_LABEL, isChiefOfStaffProfile } from '../agent/chiefOfStaff'
import TalkButton from '../agent/components/TalkButton'
import type { AgentOutputItem, AgentSessionListItem, AgentSidePanelTab, AgentComposerHandle } from '../agent'
import VerticalSplitOutlinedIcon from '@mui/icons-material/VerticalSplitOutlined'
import AgentAttachmentPreview from '../components/agent/AgentAttachmentPreview'
import { buildAgentSidePanelTabs, type PanelSessionValues } from '../components/agent/agentSidePanelTabs'
import { useDisplayNames, useJobLabel } from '../utils/displayNames'
import useComposerChips, { type ComposerChip } from '../hooks/useComposerChips'
import { AGENT_COLUMN_MAX_WIDTH } from '../components/agent/layout'
import { ArtifactPreviewDialog, ArtifactViewer, useStoryDraft } from '../agent'
import StoryEditorScreen from '../components/agent/StoryEditorScreen'
import { canEditStoryBoard } from '../utils/storyBoardEditing'
import { datavizTypeForArtifact } from '../utils/dashboardType'
import PublishWebappDialog from '../components/agent/PublishWebappDialog'
import ShareArtifactDialog from '../components/agent/ShareArtifactDialog'
import { makeRenderPlatinumData } from '../components/agent/renderArtifactData'
import { makeChatDatasetLoader, makeChatScriptLoader } from '../plugins/compositeTablePlugin'
import { AGENT_PLUGINS, getAuthToken, platinumV2Endpoints } from '../utils/agentTransport'
import { ptToolFormatter } from '../plugins/ptToolFormatter'
import { refuse } from '../utils/permissionReason'
import SessionProcessControl from '../components/agent/SessionProcessControl'
import type { MenuAction } from '../components/system/MenuActionsButton'
import { TableCitationModal } from '../components/dashboard/widgets/TableCitationModal'
import { renderCitationText } from '../components/citations/CitationMarker'
import { createCitationNumbering, type CitationCell } from '../utils/citations'
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined'
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined'
import NotebookDialog from '../components/agent/notebook/NotebookDialog'
import useNotebook from '../components/agent/notebook/useNotebook'
import { hasDataQuery } from '../components/agent/notebook/notebookCells'
import { warehouseCardActionReason } from '../components/agent/WarehouseQueryCard'
import SessionSettingsDialog, { type ChatWidth } from '../components/agent/SessionSettingsDialog'
import SavedSearchIcon from '@mui/icons-material/SavedSearch'
import Button from '@mui/material/Button'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { buildVoiceTools } from '../voice/buildVoiceTools'
import { pageHelperTitle } from '../agent/pageHelpers'
import { refreshTablesAfterTurn } from '../components/agent/refreshTablesAfterTurn'
import { buildTranscribeEndpoint } from '../hooks/useTranscribeEndpoint'
import { useGuidedMine } from '../hooks/useGuidedResponse'
import GuidedResumeBox from '../components/guided/GuidedResumeBox'
import { resumableGuides } from '../utils/guided'
import PresentationAskDialog from '../components/agent/PresentationAskDialog'
import usePresentationAsk from '../hooks/usePresentationAsk'
import { webappEntryRelPath } from '../agent/artifactFilters'
import type { DeckAsk } from '../agent/presentationFrame'

/** A presentation's Ask, with what to close once the note is in the composer (the viewer it came from). */
type AskFromPageHandler = (sessionId: string, artifact: ArtifactInfo, ask: DeckAsk, closeSource?: () => void) => void

/**
 * When the left panel starts its slide after the session page mounts, in ms
 * (#1347 item 2b). The conversation paints first, then the panel arrives.
 * 0 under reduced motion. The right panel does not open by itself: the
 * conversation keeps that width, and the preview pills in the chat's corner
 * say what the panel holds (Adam, 12 Sep).
 */
export const PANEL_ARRIVAL_MS = { left: 450 } as const

const SESSION_PAGE_SIZE = 50

/**
 * The badge a page helper chat wears when it is opened here, on the agent
 * page: it began beside a page, and here no page tools are sent.
 */
export function pageHelperNotice(profile: unknown): ChatNoticeProps | undefined {
  const title = typeof profile === 'string' ? pageHelperTitle(profile) : undefined
  return title ? { severity: 'info', text: `${title} helper chat. Opened here, it has no access to the page's controls.` } : undefined
}

// Module constants so the provider config keeps a stable identity for the
// pieces useAgentSession keys effects off. Chips change on every table click,
// which recomputes the config object — the endpoints/plugins/models it carries
// must not change with it or the live session would be torn down mid-stream.
// The model offering is no longer a module constant (the server owns it), but
// useAgentModels holds it in state and sets it once, so it is just as stable.
export { AGENT_PLUGINS }
const AGENT_TOOL_FORMATTERS = [ptToolFormatter]

/**
 * The side panel's session values, as a hook the PANEL calls (inside the chat
 * provider), bound to the page's route-level fallbacks. A factory rather than
 * a `useCallback` whose body calls useAgentChat: that shape ran correctly, but
 * the hook call sat in an anonymous callback, which rules-of-hooks rejects.
 * Here it sits in a function named like the hook it is. The page memoises the
 * result on the same two values, so its identity changes exactly as before.
 */
function makePanelValuesHook(sessionId: string | undefined, sessionJob: string | undefined) {
  return function usePanelValues(): PanelSessionValues {
    const { session, sessionJobs, setSessionJobs, sendMessage, installedSkills, hoistedSkill, messages, isStreaming } = useAgentChat()
    return {
      sessionId: sessionId || session?.id || '',
      sessionJobs,
      fallbackJobs: session?.jobs || [],
      // A user's tick lands in the same state an agent's set_jobs lands in, so
      // the Tables and Variables job pickers narrow immediately too.
      onJobsChanged: setSessionJobs,
      job: session?.job || sessionJob || '',
      treeJobs: sessionJobs ?? session?.jobs ?? [],
      onSendMessage: (message: string) => sendMessage(message),
      installedSkillNames: new Set((installedSkills ?? []).map((s) => s.name)),
      hoistedSkill,
      hasCompletedTurn: hasCompletedTurn(messages, isStreaming),
    }
  }
}
// The conversation is a sheet, not the whole width between the panels: it keeps
// the start screen's column width so beat 3 reads as that column rising out of
// the bottom edge, an inverted notch, rather than the page changing shape.
/**
 * The reader's chat width, remembered per browser (Adam, 9 Sep): the centred
 * column by default, or full width, as the very old chat once was.
 */
const CHAT_WIDTH_KEY = 'platinum.agent.chatWidth'
function readChatWidth(): ChatWidth {
  try { return window.localStorage.getItem(CHAT_WIDTH_KEY) === 'full' ? 'full' : 'column' } catch { return 'column' }
}
function writeChatWidth(w: ChatWidth) {
  try { window.localStorage.setItem(CHAT_WIDTH_KEY, w) } catch { /* private mode: the choice lasts the session */ }
}

/**
 * Beat 3 of the start screen's Send (#1294): the start screen leaves a one-shot
 * marker saying "this session was opened by Send", and the conversation column
 * rises out of the bottom of the screen to meet the composer that is already
 * sitting there. Reading it removes it, so a reload, a deep link or a history
 * navigation gets no entrance — which is the point, because in those cases the
 * reader has not just watched a composer travel down the page.
 */
const RISE_MARKER_PREFIX = 'agent-enter-rise-'

function takeRiseMarker(sessionId?: string): boolean {
  if (!sessionId) return false
  try {
    const key = `${RISE_MARKER_PREFIX}${sessionId}`
    if (sessionStorage.getItem(key) === null) return false
    sessionStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

function AgentChatInner({ sessionId, customer, openArtifactId, onArtifactParamUsed, onOpenArtifactViewer, onShareArtifact, shareRefusal, onPublishWebapp, onEditStory, onAskFromPage, onStartChatFromPage, readOnly, artifactPanelOpen, onOpenArtifactPanel, onCloseArtifactPanel, composerHandleRef, composerLeading, agentRefusal, releaseNotice }: {
  sessionId?: string
  customer?: string
  /** The URL's ?artifact= (#1649): open this artifact's preview once it is in the list. */
  openArtifactId?: string
  /** The preview for ?artifact= is open; the page takes the param out of the URL. */
  onArtifactParamUsed?: () => void
  onOpenArtifactViewer?: (a: ArtifactInfo | null) => void
  /** Share by link (Adam, 13 Sep): the page owns the dialog; this hands it the artifact. */
  onShareArtifact?: (sessionId: string, artifact: ArtifactInfo) => void
  shareRefusal?: string
  /** Share a web app: publish it to DataViz (#1578). The page owns the dialog. */
  onPublishWebapp?: (sessionId: string, artifact: ArtifactInfo) => void
  /** Edit a story board in place (#1416). Passed only to the session owner or a Manager. */
  onEditStory?: (sessionId: string, artifact: ArtifactInfo) => void
  /** A presentation's Ask (presentations): the page owns the dialog. Ask is closed with `agentRefusal`. */
  onAskFromPage?: AskFromPageHandler
  /** "Start a new chat from here" on a story board (the viewer's toolbar). Closed with `agentRefusal`. */
  onStartChatFromPage?: (sessionId: string, artifact: ArtifactInfo) => void
  readOnly?: boolean
  artifactPanelOpen: boolean
  composerHandleRef?: React.MutableRefObject<AgentComposerHandle | null>
  onOpenArtifactPanel: () => void
  onCloseArtifactPanel: () => void
  /** The Session options gear, drawn in the composer's leading slot. */
  composerLeading?: React.ReactNode
  /** Why this person may not send (#1537 AI access), or undefined. Reading the session stays open. */
  agentRefusal?: string
  /** A release is a few minutes away (a recent `release_pending` bell row): its message. */
  releaseNotice?: string
}) {
  const { resumeSession, sendMessage, session, artifacts, setSelectedModel, sessionJobs, error: turnError, errorCode: turnErrorCode, isStreaming, pluginEvents, messages, askedQuestions, queuedMessages, selectedModel } = useAgentChat()
  // The update banner sits above every provider, so it learns from here when
  // a turn is answering and holds its reload offer until the turn ends.
  useEffect(() => {
    setTurnActive(isStreaming)
    return () => setTurnActive(false)
  }, [isStreaming])
  // A send-turn 403 with a code (a team's budget exhausted mid-conversation,
  // say — any code, so a new refusal code needs no change here) reads
  // the same as the pre-emptive `agentRefusal` check: both are an AI access
  // refusal, so both draw the same notice card (#1537 PR3). The pre-emptive
  // check wins when both are somehow present — it is the more current answer,
  // read fresh from `/me` rather than a stale turn's error. With no refusal,
  // a release about to happen takes the card.
  const turnRefusal = turnErrorCode ? turnError ?? undefined : undefined
  const chatNotice: ChatNoticeProps | undefined = (agentRefusal ?? turnRefusal)
    ? { severity: 'error', text: (agentRefusal ?? turnRefusal) as string }
    : releaseNotice ? { severity: 'info', text: releaseNotice }
    : pageHelperNotice(session?.metadata?.profile)
  const resumedRef = useRef<string | null>(null)
  // The header's job pill reads what THIS session works on (#1347), the way
  // a dashboard page publishes its own job: the live set from the event
  // stream when there is one, else the session row's. Cleared on leave so
  // the next page does not inherit it.
  const { setPageJobs } = usePageJob()
  const headerJobs = sessionJobs ?? (session?.jobs?.length ? session.jobs : (session?.job ? [session.job] : []))
  const headerJobsKey = headerJobs.join('\u0000')
  useEffect(() => {
    setPageJobs(headerJobs)
    return () => setPageJobs([])
  }, [headerJobsKey])
  // Read once per session id, in the render phase, so the entrance is decided
  // before the column is ever drawn. Same one-shot-per-key guard the rest of
  // this page uses, and it survives a re-render without re-reading storage.
  const riseRef = useRef<{ sessionId?: string; rise: boolean }>({ rise: false })
  if (riseRef.current.sessionId !== sessionId) {
    riseRef.current = { sessionId, rise: takeRiseMarker(sessionId) }
  }
  const [viewerOpen, setViewerOpen] = useState(false)
  // Edit from the full viewer closes it: the editor takes the space beside
  // the conversation.
  const handleEditFromViewer = useCallback((sid: string, artifact: ArtifactInfo) => {
    setViewerOpen(false)
    onEditStory?.(sid, artifact)
  }, [onEditStory])

  // AgentChat calls onOpenArtifactViewer(artifact) to preview a single artifact
  // (handled by the page-level ArtifactPreviewDialog) and onOpenArtifactViewer(null)
  // for "View all artifacts" — open the full-screen viewer over the live artifact list.
  const handleOpenArtifactViewer = useCallback((a: ArtifactInfo | null) => {
    if (a) onOpenArtifactViewer?.(a)
    else setViewerOpen(true)
  }, [onOpenArtifactViewer])

  // ?artifact=<id> (#1649, a past-work search result): open that artifact's
  // preview once, when the session's artifact list holds it. The ref keeps a
  // re-render or a refreshed list from reopening it; the page then drops the
  // param, which resets the guard so the same link works again later.
  const openedArtifactRef = useRef<string | null>(null)
  useEffect(() => {
    if (!openArtifactId) {
      openedArtifactRef.current = null
      return
    }
    if (openedArtifactRef.current === openArtifactId) return
    const target = artifacts.find(a => a.id === openArtifactId)
    if (!target) return
    openedArtifactRef.current = openArtifactId
    onOpenArtifactViewer?.(target)
    onArtifactParamUsed?.()
  }, [openArtifactId, artifacts])

  useEffect(() => {
    if (!sessionId || resumedRef.current === sessionId) return
    resumedRef.current = sessionId
    resumeSession(sessionId)
  }, [sessionId])

  useEffect(() => {
    if (!sessionId || !session || session.id !== sessionId) return
    const key = `agent-initial-payload-${sessionId}`
    const raw = sessionStorage.getItem(key)
    if (!raw) return
    sessionStorage.removeItem(key)
    try {
      const { message, model, attachmentIds } = JSON.parse(raw) as { message: string; model?: string; attachmentIds?: string[] }
      // Adopt the model the session was created with, not just send the first
      // message as it. Without this the picker kept whatever it started with
      // and showed a different model than the session was actually running —
      // and the NEXT message would have been sent as that wrong one.
      if (model) setSelectedModel(model)
      // The start screen uploaded its attachments before handing over; the
      // first message carries their ids or the agent never sees the files.
      if (message) sendMessage(message, model, attachmentIds)
    } catch { /* ignore */ }
  }, [sessionId, session])

  const authHeader = axios.defaults.headers.common['Authorization'] as string | undefined

  // --- Notebook (#1869) ---
  // Cells are folded from the same plugin events the chat's cards draw. The
  // owner or a Manager edits; anyone else who can read the chat reads it.
  const cardHost = useAgentCardHost()
  const notebook = useNotebook({
    customer,
    sessionId,
    canEdit: !readOnly,
    pluginEvents,
    composerHandleRef: readOnly ? undefined : composerHandleRef,
  })
  // The cards reach the Notebook through the card host. A stable function, so
  // the memoised message rows do not redraw on every render of this page.
  const openNotebookRef = useRef(notebook.handleOpen)
  openNotebookRef.current = notebook.handleOpen
  const openNotebook = useCallback((cellId?: string) => openNotebookRef.current(cellId), [])
  const notebookHost = useMemo(() => ({ ...cardHost, openNotebook }), [cardHost, openNotebook])
  const notebookReadOnlyReason = readOnly
    ? refuse('You can’t edit this chat’s notebook.', cardHost.whoToAsk, { owner: cardHost.sessionOwner }).reason
    : undefined
  const loadNotebookDataset = useMemo(() => makeChatDatasetLoader(sessionId ?? '', API_BASE_URL), [sessionId])
  const loadNotebookScript = useMemo(() => makeChatScriptLoader(sessionId ?? '', API_BASE_URL), [sessionId])
  // "Show in chat": close the Notebook and bring that tool call's card into view.
  const handleOpenInChat = (toolCallId: string) => {
    notebook.handleClose()
    requestAnimationFrame(() => {
      document.querySelector(`[data-tool-call-id="${CSS.escape(toolCallId)}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    })
  }
  // The button shows once the chat has run a data query (Adam, 5 Nov 2026).
  // Tables and charts still open the Notebook from their own "Open in notebook".
  const notebookButton = sessionId && hasDataQuery(notebook.cells) ? (
    <Tooltip title="Notebook">
      <IconButton
        size="small"
        aria-label="Notebook"
        data-testid="agent-notebook-button"
        onClick={() => openNotebook()}
        sx={{ width: 34, height: 34, flexShrink: 0, color: 'info.main' }}
      >
        <NotesOutlinedIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>
  ) : null

  // Talk (T20): what the person says is sent as this chat's next message, with
  // the model the composer shows.
  const talkSession = {
    session,
    messages,
    isStreaming,
    error: turnError,
    askedQuestions,
    queuedMessages,
    sendMessage: (text: string) => sendMessage(text, selectedModel || undefined),
  }
  const talkButton = customer && !readOnly
    ? <TalkButton session={talkSession} customer={customer} disabledReason={agentRefusal} />
    : null

  return (
    <AgentCardHostContext.Provider value={notebookHost}>
      <AgentChat
        apiBaseUrl={API_BASE_URL}
        authHeader={authHeader}
        onOpenArtifactViewer={handleOpenArtifactViewer}
        onPublishWebapp={customer ? onPublishWebapp : undefined}
        onEditStory={onEditStory}
        onAskFromPage={onAskFromPage}
        askFromPageRefusal={agentRefusal}
        readOnly={readOnly}
        artifactPanelOpen={artifactPanelOpen}
        onOpenArtifactPanel={onOpenArtifactPanel}
        onCloseArtifactPanel={onCloseArtifactPanel}
        enterAnimation={riseRef.current.rise ? 'rise' : undefined}
        composerDisabled={!!agentRefusal}
        composerPlaceholder={agentRefusal}
        composerHandleRef={composerHandleRef}
        composerLeading={<>{composerLeading}{notebookButton}{talkButton}</>}
        notice={chatNotice}
        // POST /transcribe meters the audio to a customer and refuses a call
        // without one (security audit C23), so the mic names this page's
        // customer and the session's job; AgentChat's default is bare.
        transcribeEndpoint={buildTranscribeEndpoint(customer || session?.customer, session?.job)}
      />
      {/* A reader has no composer, so the Notebook opens from a button of its own. */}
      {readOnly && notebookButton && (
        <Box sx={{ position: 'absolute', bottom: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 2 }}>
          <Button
            variant="outlined"
            color="info"
            size="small"
            startIcon={<NotesOutlinedIcon />}
            onClick={() => openNotebook()}
            data-testid="agent-notebook-button-reader"
            sx={{ borderRadius: PILL_RADIUS, bgcolor: 'background.paper', '&:hover': { bgcolor: 'background.paper' } }}
          >
            Notebook
          </Button>
        </Box>
      )}
      {sessionId && (
        <NotebookDialog
          notebook={notebook}
          customer={customer}
          sessionId={sessionId}
          readOnlyReason={notebookReadOnlyReason}
          // Worded only while the Notebook is open, not on every render of the chat.
          queryActionReason={notebook.open ? warehouseCardActionReason(cardHost) : undefined}
          loadDataset={loadNotebookDataset}
          loadScript={loadNotebookScript}
          onOpenInChat={handleOpenInChat}
        />
      )}
      {viewerOpen && sessionId && (
        <ArtifactViewer
          artifacts={artifacts}
          sessionId={sessionId}
          customer={customer}
          apiBaseUrl={API_BASE_URL}
          authHeader={authHeader}
          onClose={() => setViewerOpen(false)}
          onPublishWebapp={customer ? onPublishWebapp : undefined}
          onShareArtifact={onShareArtifact}
          shareRefusal={shareRefusal}
          onEditStory={onEditStory ? handleEditFromViewer : undefined}
          renderPlatinumData={makeRenderPlatinumData(sessionId)}
          // Asking in this chat closes the viewer, so the note is seen in the composer.
          onAskFromPage={onAskFromPage ? (sid, artifact, ask) => onAskFromPage(sid, artifact, ask, () => setViewerOpen(false)) : undefined}
          askFromPageRefusal={agentRefusal}
          onStartChatFromPage={onStartChatFromPage}
          startChatFromPageRefusal={agentRefusal}
        />
      )}
    </AgentCardHostContext.Provider>
  )
}

/**
 * The chat's process badge and three-dots menu, with the live streaming flag
 * from chat context. Must live inside <AgentChatProvider> to read isStreaming.
 */
function ActiveSessionControl({ customer, sessionId, stopRefusal, onLiveStateChange, extraActions }: {
  customer: string
  sessionId: string
  stopRefusal?: string
  onLiveStateChange?: (containerState: string) => void
  extraActions?: MenuAction[]
}) {
  const { isStreaming } = useAgentChat()
  return (
    <SessionProcessControl
      customer={customer}
      sessionId={sessionId}
      chatStreaming={isStreaming}
      stopRefusal={stopRefusal}
      onLiveStateChange={onLiveStateChange}
      extraActions={extraActions}
    />
  )
}

export default function AgentChatPageV2() {
  const router = useRouter()
  const account = useAccount()
  const snackbarTopOffset = useSnackbarTopOffset()
  const customer = (router.params.customer as string | undefined) || account.customer?.name || ''
  const labelJob = useJobLabel(customer)
  // Douglas says this aloud, so it is the customer's name, not its code.
  const customerLabel = useDisplayNames(customer).customer
  // Unfinished guided conversations, offered at the top of History
  // (Matt, 29 Oct 2026): guided chats are kept out of the chat list.
  const resumable = resumableGuides(useGuidedMine(customer).items)
  const sessionId = (router.params.sessionId as string | undefined) || undefined
  // AI access (#1537): sending needs level 3. Below it the conversation stays
  // readable and the composer says why it is closed; the server refuses too.
  const aiAccess = useAiAccess(customer)
  const agentRefusal = aiAccess.can('agent_sessions') ? undefined : aiAccess.refusalFor('agent_sessions')
  // A release a few minutes away, from the bell's feed: shown above the
  // composer, and the chat says "Platinum is updating" while it reconnects.
  const releaseNotice = useReleaseNotice(customer)

  // --- Session list state (same pattern as v1) ---
  const [sessionList, setSessionList] = useState<AgentSessionListItem[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [sessionUsers, setSessionUsers] = useState<string[]>([])
  // #1273 D1: both panels are hidden on every load, for everyone. Deliberately
  // NOT persisted — a demo must not open with the last user's session history on
  // screen. They are independent: opening one never closes the other.
  //
  // The left panel opens by default when a session page mounts (Matt, 11
  // Sep, #1347: a user must be able to see which job the session is on, and
  // the edge strips alone did not get them there). It ARRIVES rather than
  // appears (#1347 item 2b): the page paints with the conversation alone,
  // then the panel slides in. Matt: coming off the start screen, "if the
  // panels open sweetly it lands, and if they just appear it doesn't". On
  // the first tick it was spent before anyone was looking. Under reduced
  // motion there is no ceremony to stage, so it opens at once.
  //
  // The right panel stays closed until asked (Adam, 12 Sep): the conversation
  // keeps the width, and the preview pills in the chat's top-right corner say
  // what the panel holds. Closing is the user's, and the choice lasts as long
  // as the page.
  const [leftOpen, setLeftOpen] = useState(false)
  // The tab the left panel shows when the page picks one (the launcher's dial,
  // a voice tool); undefined = the panel's default.
  const [leftTab, setLeftTab] = useState<string | undefined>(undefined)
  const [rightOpen, setRightOpen] = useState(false)
  useEffect(() => {
    const reduced = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const left = window.setTimeout(() => setLeftOpen(true), reduced ? 0 : PANEL_ARRIVAL_MS.left)
    return () => window.clearTimeout(left)
    // Mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [userFilter, setUserFilter] = useState('me')
  const [searchQuery, setSearchQuery] = useState('')
  // The Page helpers filter ('' = page helper chats left out), sent as ?page=.
  const [pageFilter, setPageFilter] = useState('')
  const pageFilterRef = useRef('')
  const [isSearching, setIsSearching] = useState(false)
  // The search the list is showing (#1634). The server filters the list, so
  // every fetch below, the 15s refresh included, carries it; otherwise the
  // refresh would replace a search with the whole list.
  const appliedQueryRef = useRef('')
  const [previewArtifact, setPreviewArtifact] = useState<ArtifactInfo | null>(null)
  // Share by link (Adam, 13 Sep): the viewer and the preview dialog both
  // offer it; this page owns the dialog that mints and revokes.
  const [shareArtifact, setShareArtifact] = useState<ArtifactInfo | null>(null)
  // A web app or a video is shared by publishing it to DataViz (#1578 items
  // 12, 13): one item, one link, the item's share settings. Every Share on
  // one of them lands in the publish dialog, never the artifact link.
  const [publishArtifact, setPublishArtifact] = useState<ArtifactInfo | null>(null)
  const handlePublishWebapp = useCallback((_sid: string, artifact: ArtifactInfo) => {
    setPublishArtifact(artifact)
  }, [])
  const handleShareArtifact = useCallback((_sid: string, artifact: ArtifactInfo) => {
    if (datavizTypeForArtifact(artifact)) setPublishArtifact(artifact)
    else setShareArtifact(artifact)
  }, [])
  const [snapToast, setSnapToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null)
  const [optionsOpen, setOptionsOpen] = useState(false)
  // The microphone choice for dictation lives in Session options (Adam, 14 Sep).
  const microphones = useMicrophones()
  const [indexing, setIndexing] = useState(false)
  const isManager = account.user?.role_permissions?.isManager ?? false
  const [chatWidth, setChatWidth] = useState<ChatWidth>(readChatWidth)
  const setChatWidthPersisted = (next: ChatWidth) => { writeChatWidth(next); setChatWidth(next) }
  const setChatWidthTo = (next: ChatWidth) => { writeChatWidth(next); setChatWidth(next) }

  // The model offering comes from the server, which also gates every message
  // against it — so the picker cannot show something the backend would refuse.
  // Declared before the voice manifest below, which offers the same list.
  const agentModels = useAgentModels()

  // Voice manifest (#1323): Douglas types into the composer, sends, and opens
  // the panels, through the same handlers the buttons use. The composer lives
  // in chat-ui; it hands the page a live handle.
  const composerHandleRef = useRef<AgentComposerHandle | null>(null)
  useVoiceRegistration({
    pageKey: `agent-session:${customer}:${sessionId ?? ''}`,
    pageContext: () =>
      `An agent session for ${customerLabel}. The composer is docked at the bottom; type there and Send. ` +
      `Left edge: the side panel with session history (previous sessions) and a Tables tab (the project's tables; clicking one adds it to the message so the agent reads it). ` +
      `Right edge: the Files panel (everything the agent has made; a web app there has Share, which publishes it to DataViz). ` +
      `The composer toolbar has the attachment clip and the microphone. Chat width is ${chatWidth}. Composer text right now: ${composerHandleRef.current?.getInput().trim() ? JSON.stringify(composerHandleRef.current.getInput()) : '(empty)'}.`,
    uiTools: buildVoiceTools({
      fields: [{ name: 'set_message', label: 'the message in the composer', set: (v) => composerHandleRef.current?.setInput(v) }],
      choices: [{
        name: 'choose_model',
        label: 'the AI model the next message uses (the picker in the composer toolbar)',
        // Douglas sees the tier names (Best, Good), never a model id.
        options: agentModels.models.map((m) => ({ value: m.label, describe: m.description || m.label })),
        select: (v) => {
          const id = agentModels.models.find((m) => m.label === v)?.id
          if (!id) return `no model called ${v}`
          composerHandleRef.current?.setModel(id)
          return `model set to ${v}`
        },
      }, {
        name: 'set_chat_width',
        label: 'the chat width',
        options: [{ value: 'full', describe: 'edge to edge' }, { value: 'column', describe: 'a centred column' }],
        select: (v) => { setChatWidthTo(v === 'full' ? 'full' : 'column'); return `chat width ${v}` },
      }],
      actions: [
        { name: 'send_message', label: 'send the message in the composer', run: () => composerHandleRef.current?.send(), enabled: () => !!composerHandleRef.current?.getInput().trim(), note: 'Only when the composer has text.' },
        { name: 'open_session_history', label: 'open the left panel: session history and the Tables tab', run: () => { setLeftTab(SIDE_PANEL_HISTORY_KEY); setLeftOpen(true) } },
        { name: 'open_tables_panel', label: 'open the left panel on its Tables tab', run: () => { setLeftTab('toc'); setLeftOpen(true) } },
        { name: 'close_tables_panel', label: 'close the left panel (session history and Tables)', run: () => setLeftOpen(false) },
        { name: 'open_files_panel', label: 'open the Files panel on the right', run: () => setRightOpen(true) },
        { name: 'close_files_panel', label: 'close the Files panel', run: () => setRightOpen(false) },
      ],
    }),
  })
  // #1296: a numbered citation in an agent answer opens the same table popup
  // the Gen AI cards use. The table lives in the session's job.
  const [citation, setCitation] = useState<{ table: string; cell?: CitationCell } | null>(null)

  const handleIndexConversation = useCallback(async () => {
    if (!sessionId) return
    setIndexing(true)
    try {
      await axios.post(`${API_BASE_URL}/agent/session/${sessionId}/index`, {})
      setSnapToast({ msg: 'Conversation queued for indexing — searchable shortly', sev: 'success' })
    } catch {
      setSnapToast({ msg: 'Failed to queue conversation for indexing', sev: 'error' })
    } finally {
      setIndexing(false)
    }
  }, [sessionId])
  // Manager-only extra for the chat's three-dots menu, below Stop.
  const adminMenuActions: MenuAction[] = useMemo(() => isManager
    ? [{ title: 'Index conversation', icon: <SavedSearchIcon fontSize="small" />, onClick: handleIndexConversation, disabled: indexing }]
    : [], [isManager, indexing, handleIndexConversation])

  const sessionListRef = useRef(sessionList)
  sessionListRef.current = sessionList

  const currentUserEmail = account.user?.session_info?.email || undefined

  const fetchSessions = useCallback(async (userEmail?: string) => {
    const query = appliedQueryRef.current
    try {
      const params = new URLSearchParams({ limit: String(SESSION_PAGE_SIZE) })
      if (customer) params.set('customer', customer)
      if (userEmail) params.set('user_email', userEmail)
      if (query) params.set('q', query)
      if (pageFilterRef.current) params.set('page', pageFilterRef.current)
      params.set('exclude_workflow_prefix', 'assistant-')
      const resp = await axios.get<{ sessions: AgentSessionListItem[] }>(
        `${API_BASE_URL}/agent/sessions?${params.toString()}`
      )
      // The search changed while this was in flight: a later fetch owns the list.
      if (appliedQueryRef.current !== query) return
      const sessions = resp.data.sessions || []
      setSessionList(sessions)
      setHasMore(sessions.length >= SESSION_PAGE_SIZE)
    } catch (err) {
      console.error('Failed to list sessions:', err)
    }
  }, [customer])

  const loadMore = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        has_messages: 'true',
        limit: String(SESSION_PAGE_SIZE),
        offset: String(sessionListRef.current.length),
      })
      if (customer) params.set('customer', customer)
      if (userFilter === '*') params.set('user_email', '*')
      else if (userFilter !== 'me') params.set('user_email', userFilter)
      if (appliedQueryRef.current) params.set('q', appliedQueryRef.current)
      if (pageFilterRef.current) params.set('page', pageFilterRef.current)
      params.set('exclude_workflow_prefix', 'assistant-')
      const resp = await axios.get<{ sessions: AgentSessionListItem[] }>(
        `${API_BASE_URL}/agent/sessions?${params.toString()}`
      )
      const more = resp.data.sessions || []
      setSessionList(prev => [...prev, ...more])
      setHasMore(more.length >= SESSION_PAGE_SIZE)
    } catch (err) {
      console.error('Failed to load more sessions:', err)
    }
  }, [customer, userFilter])

  const loadUsers = useCallback(async () => {
    try {
      const resp = await axios.get<{ users: string[] }>(
        `${API_BASE_URL}/agent/sessions/users?customer=${customer}`
      )
      setSessionUsers(resp.data.users || [])
    } catch { /* ignore */ }
  }, [customer])

  // Load sessions on mount and when filter changes
  const loadedRef = useRef('')
  useEffect(() => {
    const key = `${customer}:${userFilter}`
    if (loadedRef.current === key) return
    loadedRef.current = key
    const email = userFilter === 'me' ? undefined : userFilter === '*' ? '*' : userFilter
    fetchSessions(email)
    loadUsers()
  }, [customer, userFilter, fetchSessions, loadUsers])

  // Refresh session list periodically while on the page
  useEffect(() => {
    const interval = setInterval(() => {
      const email = userFilter === 'me' ? undefined : userFilter === '*' ? '*' : userFilter
      fetchSessions(email)
    }, 15000)
    return () => clearInterval(interval)
  }, [userFilter, fetchSessions])

  const sessionEmailParam = userFilter === 'me' ? undefined : userFilter

  // Past-work search (#1649): the outputs past chats made, searched beside
  // the chats with the same query and user filter.
  const { outputs, outputsNotice, handleSearchOutputs, handleClearOutputs } = useOutputSearch(customer)

  const handleUserFilterChange = (value: string) => {
    setUserFilter(value)
    setSearchQuery('')
    appliedQueryRef.current = ''
    handleClearOutputs()
  }

  const handlePageFilterChange = (value: string) => {
    pageFilterRef.current = value
    setPageFilter(value)
    void fetchSessions(sessionEmailParam)
  }

  const handleSearchChange = (value: string) => {
    setSearchQuery(value)
    if (!value.trim()) handleClearOutputs()
    if (!value.trim() && appliedQueryRef.current) {
      appliedQueryRef.current = ''
      fetchSessions(sessionEmailParam)
    }
  }

  const handleSearchSubmit = async (query: string) => {
    const trimmed = query.trim()
    if (trimmed.length < 2) return
    appliedQueryRef.current = trimmed
    setIsSearching(true)
    void handleSearchOutputs(trimmed, userFilter)
    await fetchSessions(sessionEmailParam)
    setIsSearching(false)
  }

  // An artifact opens its chat with the preview (?artifact=); a table opens Tables.
  const handleSelectOutput = (output: AgentOutputItem) => {
    const dest = outputDestination(output, customer)
    router.navigate(dest.name, dest.params)
  }

  const handleOpenLeftPanel = useCallback((tab: string) => {
    setLeftTab(tab)
    setLeftOpen(true)
  }, [])
  // The left-edge pill reopens the panel on the tab it last showed.
  const handleRevealLeftPanel = useCallback(() => setLeftOpen(true), [])
  const handleCloseLeftPanel = useCallback(() => setLeftOpen(false), [])
  const handleOpenRightPanel = useCallback(() => setRightOpen(true), [])
  const handleCloseRightPanel = useCallback(() => setRightOpen(false), [])

  const handleSelectSession = useCallback((sid: string) => {
    router.navigate('agent-session', { customer, sessionId: sid })
  }, [router, customer])

  // Rename, star and delete from the History tab (#1634). Deleting the chat
  // that is open leaves for the start screen.
  const whoToAsk = useWhoToAsk(customer)
  const { handleRenameSession, handleToggleStar, handleDeleteSession } = useChatHistoryActions({
    refetch: () => fetchSessions(sessionEmailParam),
    onError: msg => setSnapToast({ msg, sev: 'error' }),
    onDeleted: sid => { if (sid === sessionId) router.navigate('agent', { customer }) },
  })

  // --- Session job resolution (for the left panel's Tables/Variables tabs) ---
  // Prefer the matching entry already in sessionList (populated by the list
  // fetch above); fall back to a direct session fetch for deep links, where a
  // user lands straight on /agent/:customer/:sessionId with no session list
  // loaded yet, or the session is older than the fetched page.
  const sessionFromList = useMemo(
    () => sessionList.find(s => s.id === sessionId),
    [sessionList, sessionId]
  )
  const [fetchedSessionJob, setFetchedSessionJob] = useState<{ sessionId: string; job: string; owner: string; profile: string } | null>(null)
  useEffect(() => {
    if (!sessionId || sessionFromList?.job) return
    let cancelled = false
    axios.get<{ job?: string; user_email?: string; metadata?: { profile?: unknown } }>(`${API_BASE_URL}/agent/session/${sessionId}`)
      .then(resp => {
        if (cancelled) return
        const profile = resp.data.metadata?.profile
        setFetchedSessionJob({ sessionId, job: resp.data.job || '', owner: resp.data.user_email || '', profile: typeof profile === 'string' ? profile : '' })
      })
      .catch(() => { if (!cancelled) setFetchedSessionJob({ sessionId, job: '', owner: '', profile: '' }) })
    return () => { cancelled = true }
  }, [sessionId, sessionFromList?.job])
  const sessionJob = sessionFromList?.job
    || (fetchedSessionJob && fetchedSessionJob.sessionId === sessionId ? fetchedSessionJob.job : '')
    || ''
  // The chat's profile, for Session options' read-only Mode row (Chief of
  // Staff, design/2026-10-26-chief-of-staff-agents-panel.md T1). The dialog
  // is drawn here, outside the provider, so it reads the list or the fetch.
  const sessionProfile = sessionFromList?.profile
    || (fetchedSessionJob && fetchedSessionJob.sessionId === sessionId ? fetchedSessionJob.profile : '')

  // --- Story board editor (#1416) ---
  // The session owner or a Manager edits a story board in place. Which board
  // is open lives in the URL (?editStory=<artifactId>), so a dashboard's
  // "Edit story board" can link straight here and Back closes it. The editor
  // is full screen, over the whole page (StoryEditorScreen); the session
  // underneath keeps its panels as they were, so Close returns to them.
  const sessionOwner = sessionFromList?.user_email
    || (fetchedSessionJob && fetchedSessionJob.sessionId === sessionId ? fetchedSessionJob.owner : '')
  const canEditStories = canEditStoryBoard(isManager, sessionOwner, currentUserEmail)
  // What an inline card (the warehouse query card) may know about this chat and
  // its viewer; the plugin render context carries only the session id.
  const cardHost = useMemo(() => ({
    customer,
    viewerEmail: currentUserEmail,
    viewerIsManager: isManager,
    sessionOwner,
    whoToAsk,
    composerHandleRef,
  }), [customer, currentUserEmail, isManager, sessionOwner, whoToAsk])
  const editStoryParam = (router.params.editStory as string | undefined) || ''
  const editStoryId = canEditStories && editStoryParam && sessionId ? editStoryParam : null
  const storyDraft = useStoryDraft({ artifactId: editStoryId, apiBaseUrl: API_BASE_URL, getAuthToken })
  const handleEditStory = useCallback((_sid: string, artifact: ArtifactInfo) => {
    if (!artifact.id) return
    setPreviewArtifact(null)
    router.setParams({ editStory: artifact.id })
  }, [router])
  // Replace, not push: Back must not reopen the editor that was just closed.
  const handleCloseStoryEditor = useCallback(() => router.removeParams(['editStory'], { history: 'replace' }), [router])
  // ?artifact=<id> (#1649): a past-work result opens its chat with that
  // artifact's preview. AgentChatInner opens it once the artifact list holds
  // it; the param then leaves the URL, replace not push, as editStory does.
  const artifactParam = (router.params.artifact as string | undefined) || undefined
  const handleArtifactParamUsed = () => router.removeParams(['artifact'], { history: 'replace' })
  // Only the chat's owner or a Manager may stop its process (the same rule
  // as writing into it); anyone else sees Stop disabled with who to ask.
  const stopRefusal = !sessionOwner || canEditStories
    ? undefined
    : refuse("You can't stop this chat.", whoToAsk, { owner: sessionOwner }).reason
  // Sharing a file by link, or stopping a share, is a write on the chat
  // (security audit C14): the same owner-or-Manager rule.
  const shareRefusal = !sessionOwner || canEditStories
    ? undefined
    : refuse("You can't share this chat's files by link.", whoToAsk, { owner: sessionOwner }).reason
  // A presentation's Ask (presentations): ask here, or start a new chat with
  // a copy. Writing here is the owner's or a Manager's (audit A5); anyone who
  // can read the chat may start their own from it.
  const presentationAsk = usePresentationAsk({
    composerHandleRef,
    askInChatRefusal: !sessionOwner || canEditStories
      ? undefined
      : refuse("You can't write in this chat.", whoToAsk, { owner: sessionOwner }).reason,
    startRefusal: agentRefusal,
  })
  const handleAskFromPage: AskFromPageHandler = (_sid, artifact, ask, closeSource) => {
    presentationAsk.handleOpen({
      ask,
      customer,
      source: { kind: 'artifact', id: artifact.id ?? '' },
      job: sessionJob,
      entryPath: webappEntryRelPath(artifact),
      closeSource,
    })
  }
  // The toolbar's "Start a new chat from here": the whole story board, deck
  // or plain page, with no slide (the page's own Ask carries one).
  const handleStartChatFromPage = (_sid: string, artifact: ArtifactInfo) => {
    presentationAsk.handleStartChatFromPage({
      customer,
      source: { kind: 'artifact', id: artifact.id ?? '' },
      job: sessionJob,
      label: artifact.label || artifact.fileName,
    })
  }
  // Patch the open chat's History badge from the process control's status
  // polling, so a stop or a start shows at once instead of waiting for the
  // 15s list refresh.
  const handleLiveState = useCallback((containerState: string) => {
    setSessionList(prev => {
      const idx = prev.findIndex(s => s.id === sessionId)
      if (idx === -1 || prev[idx].container_state === containerState) return prev
      const next = [...prev]
      next[idx] = { ...next[idx], container_state: containerState }
      return next
    })
  }, [sessionId])

  // --- Composer table chips (#1273 D1) ---
  const { chips, chipIds, toggleChip, removeChip, clear: clearChips, buildMessage, suggestedPrompt } = useComposerChips()

  // What the preview dialog is showing, or null. A chip is the handle for both
  // openers — the tree builds one from the node clicked, the composer already
  // holds one — so the dialog needs to know nothing about where it came from.
  const [preview, setPreview] = useState<ComposerChip | null>(null)
  const closePreview = useCallback(() => setPreview(null), [])
  // Clicking a chip in the composer previews it. Chips reach agentkit stripped
  // to {id,label,tooltip}, so the id comes back and is resolved here.
  const handleChipClick = useCallback((id: string) => {
    setPreview(chips.find(c => c.id === id) ?? null)
  }, [chips])

  // The one tab list (agentSidePanelTabs.tsx). The session-side values are
  // read from the chat context INSIDE the panel, through this hook.
  //
  // 🔴 The chat context's session is NOT always there. On a deep link — landing
  // straight on /agent/:customer/:sessionId — it is still resolving, and until
  // it does `session?.job` is empty, so the trees would fetch for no job and
  // show an empty panel with nothing to say why. The page resolves the same
  // job by a second route (the session list) and hands it in as the fallback;
  // whichever answers first wins, and they cannot disagree because they name
  // the same session.
  const usePanelValues = useMemo(() => makePanelValuesHook(sessionId, sessionJob), [sessionId, sessionJob])
  const sidePanelTabs: AgentSidePanelTab[] = useMemo(
    () => buildAgentSidePanelTabs({ customer, chipIds, onToggle: toggleChip, onPreview: setPreview, useValues: usePanelValues }),
    [customer, chipIds, toggleChip, setPreview, usePanelValues],
  )

  const endpoints = useMemo(() => platinumV2Endpoints(customer), [customer])

  // Numbering is per MESSAGE, in first-use order: the factory is handed one
  // message's markdown and returns the renderer for its text nodes. The job
  // the table lives in is read from the session inside the provider (see
  // AgentCitationModal), so markers never depend on the session list.
  const renderInlineCitations = useCallback((content: string) => {
    const numbering = createCitationNumbering(content)
    if (numbering.references.length === 0) return undefined
    return (text: string) => renderCitationText(
      text,
      numbering,
      (reference) => setCitation({ table: reference.label, cell: reference.cell }),
    )
  }, [])

  // --- Chat provider config ---
  const config: AgentChatConfig = useMemo(() => ({
    apiBaseUrl: '',
    // Passed through whole, not remapped: rebuilding each entry as {id,label}
    // silently dropped costMultiplier and the picker lost its cost column.
    models: agentModels.models,
    defaultModel: agentModels.defaultModel,
    endpoints,
    getAuthToken,
    plugins: AGENT_PLUGINS,
    toolFormatters: AGENT_TOOL_FORMATTERS,
    sidePanelTabs,
    composer: {
      chips: chips.map(c => ({ id: c.id, label: c.label, tooltip: c.tooltip })),
      onRemoveChip: removeChip,
      onChipClick: handleChipClick,
      // Offered into an empty box only — attaching something never edits what
      // the user has already typed.
      suggestedText: suggestedPrompt,
      buildMessage,
      onSent: clearChips,
    },
    onTurnComplete: refreshTablesAfterTurn,
    reconnectLabel: releaseNotice.reconnectLabel,
    renderInlineCitations,
    // Both panels open: the composer card meets the panels on both sides, so
    // its top edge is straight rather than rounded (Adam, 10 Sep).
    sheet: { maxWidth: AGENT_COLUMN_MAX_WIDTH, fullWidth: chatWidth === 'full', squareTop: leftOpen && rightOpen },
  }), [agentModels.models, agentModels.defaultModel, endpoints, sidePanelTabs, chips, removeChip, handleChipClick, suggestedPrompt, buildMessage, clearChips, releaseNotice.reconnectLabel, renderInlineCitations, chatWidth, leftOpen, rightOpen])

  return (
    <AgentChatProvider config={config}>
      {/* No controls bar (#1347 item 5): what it carried lives in Session
          options, opened from the gear in the composer, so the conversation
          has that height back. Both side panels open in the row below. */}
      <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <SessionSettingsDialog
          open={optionsOpen}
          onClose={() => setOptionsOpen(false)}
          chatWidth={chatWidth}
          onChatWidthChange={setChatWidthPersisted}
          microphones={microphones.devices}
          microphoneId={microphones.selectedDeviceId}
          onMicrophoneChange={microphones.selectDevice}
          modeLabel={isChiefOfStaffProfile(sessionProfile) ? CHIEF_OF_STAFF_LABEL : undefined}
          sessionControl={sessionId && customer ? (
            <ActiveSessionControl
              customer={customer}
              sessionId={sessionId}
              stopRefusal={stopRefusal}
              onLiveStateChange={handleLiveState}
              extraActions={adminMenuActions}
            />
          ) : undefined}
        />
        {/* While the story board editor is open this is display: none: the
            message list (a long conversation is heavy) is neither laid out
            nor painted under the editor. It stays mounted, so the chat keeps
            its state and stream and the editor's panel sends through it. */}
        <WebappFramesPausedContext.Provider value={!!editStoryId}>
        <AgentCardHostContext.Provider value={cardHost}>
          <Box
            data-testid="agent-session-body"
            aria-hidden={editStoryId ? true : undefined}
            sx={{ flex: 1, minHeight: 0, display: editStoryId ? 'none' : 'flex', overflow: 'hidden', position: 'relative' }}
          >
            {/* The way into a closed left panel: a button in the chat's
                top-left corner whose dial names every tab (#1795 follow-up). */}
            {/* The left-edge pill stays beside the launcher (Adam, 13 Oct): it reopens the panel on its last tab. */}
            <EdgeReveal
              side="left"
              label="Open tables panel"
              icon={<VerticalSplitOutlinedIcon />}
              hidden={leftOpen}
              onOpen={handleRevealLeftPanel}
            />
            <SidePanelLauncher tabs={sidePanelTabs} hidden={leftOpen} onOpen={handleOpenLeftPanel} />
            <AgentSidePanel
              open={leftOpen}
              onClose={handleCloseLeftPanel}
              tabs={sidePanelTabs}
              activeKey={leftTab}
              onActiveKeyChange={setLeftTab}
              sessions={sessionList}
              activeSessionId={sessionId}
              onSelectSession={handleSelectSession}
              onLoadMore={loadMore}
              hasMore={hasMore}
              users={sessionUsers}
              selectedUserEmail={userFilter}
              onUserFilterChange={handleUserFilterChange}
              searchQuery={searchQuery}
              onSearchChange={handleSearchChange}
              onSearchSubmit={handleSearchSubmit}
              isSearching={isSearching}
              currentUserEmail={currentUserEmail}
              onRenameSession={handleRenameSession}
              onDeleteSession={handleDeleteSession}
              onToggleStar={handleToggleStar}
              actionReason={chatActionReason(whoToAsk, currentUserEmail, isManager)}
              labelJob={labelJob}
              outputs={outputs}
              outputsNotice={outputsNotice}
              onSelectOutput={handleSelectOutput}
              pageFilter={pageFilter}
              onPageFilterChange={handlePageFilterChange}
              pinned={<GuidedResumeBox items={resumable} onOpen={(responseId) => router.navigate('guided-response', { customer, responseId })} />}
            />
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <AgentChatInner
                sessionId={sessionId}
                customer={customer}
                openArtifactId={artifactParam}
                onArtifactParamUsed={handleArtifactParamUsed}
                onOpenArtifactViewer={setPreviewArtifact}
                onShareArtifact={handleShareArtifact}
                shareRefusal={shareRefusal}
                onPublishWebapp={handlePublishWebapp}
                onEditStory={canEditStories ? handleEditStory : undefined}
                onAskFromPage={handleAskFromPage}
                onStartChatFromPage={handleStartChatFromPage}
                // Only the owner or a Manager may write into a chat (audit A5;
                // the server refuses anyone else). canEditStories is that rule.
                readOnly={!!sessionOwner && !canEditStories}
                artifactPanelOpen={rightOpen}
                onOpenArtifactPanel={handleOpenRightPanel}
                onCloseArtifactPanel={handleCloseRightPanel}
                agentRefusal={agentRefusal}
                releaseNotice={releaseNotice.text}
                composerHandleRef={composerHandleRef}
                composerLeading={(
                  <Tooltip title="Session options">
                    <IconButton
                      size="small"
                      aria-label="Session options"
                      data-testid="agent-session-options-button"
                      onClick={() => setOptionsOpen(true)}
                      sx={{ width: 34, height: 34, flexShrink: 0, color: 'info.main', position: 'relative' }}
                    >
                      <TuneOutlinedIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                  </Tooltip>
                )}
              />
            </Box>
          </Box>
        </AgentCardHostContext.Provider>
        </WebappFramesPausedContext.Provider>
        {editStoryId && sessionId && (
          <StoryEditorScreen
            sessionId={sessionId}
            artifactId={editStoryId}
            draft={storyDraft}
            authHeader={axios.defaults.headers.common['Authorization'] as string | undefined}
            composerDisabledReason={agentRefusal}
            onClose={handleCloseStoryEditor}
            userEmail={currentUserEmail}
            onPublishNew={customer ? (artifact) => setPublishArtifact(artifact) : undefined}
          />
        )}
        {sessionId && (
          <LiveArtifactPreviewDialog
            artifact={previewArtifact}
            sessionId={sessionId}
            onClose={() => setPreviewArtifact(null)}
            apiBaseUrl={API_BASE_URL}
            authHeader={axios.defaults.headers.common['Authorization'] as string | undefined}
            onShareArtifact={handleShareArtifact}
            shareRefusal={shareRefusal}
            onPublishWebapp={customer ? handlePublishWebapp : undefined}
            onEditStory={canEditStories ? handleEditStory : undefined}
            renderPlatinumData={makeRenderPlatinumData(sessionId)}
            onAskFromPage={(sid, artifact, ask) => handleAskFromPage(sid, artifact, ask, () => setPreviewArtifact(null))}
            askFromPageRefusal={agentRefusal}
            onStartChatFromPage={handleStartChatFromPage}
            startChatFromPageRefusal={agentRefusal}
          />
        )}
        <PresentationAskDialog
          open={!!presentationAsk.request}
          breadcrumb={presentationAsk.breadcrumb}
          figureText={presentationAsk.figureText}
          question={presentationAsk.question}
          onAskInChat={presentationAsk.handleAskInChat}
          askInChatReason={presentationAsk.askInChatReason}
          onStartNewChat={presentationAsk.handleStartNewChat}
          startNewChatReason={presentationAsk.startReason}
          onClose={presentationAsk.handleClose}
        />
        {sessionId && customer && (
          <PublishWebappDialog
            open={!!publishArtifact}
            onClose={() => {
              setPublishArtifact(null)
              // Publishing from the editor changes what it shows as published.
              if (editStoryId && storyDraft.status === 'ready') void storyDraft.reload({ keepUnsaved: true })
            }}
            customer={customer}
            artifact={publishArtifact}
            defaultName={publishArtifact?.label || publishArtifact?.fileName || 'Untitled'}
            defaultDescription={publishArtifact?.description || ''}
          />
        )}
        <ShareArtifactDialog
          open={!!shareArtifact}
          onClose={() => setShareArtifact(null)}
          artifactId={shareArtifact?.id ?? null}
          label={shareArtifact?.label || shareArtifact?.fileName || 'file'}
        />
        {/* Previewing what is attached, or about to be. Rendered once at page
            level because both the side panel's trees and the composer's chips
            open it, and only one can be open at a time. */}
        <AgentAttachmentPreview chip={preview} customer={customer} onClose={closePreview} />
        {/* #1296: the cited table, opened from a numbered marker in an answer. */}
        <AgentCitationModal customer={customer} citation={citation} onClose={() => setCitation(null)} />
      </Box>
      <Snackbar open={!!snapToast} autoHideDuration={6000} onClose={() => setSnapToast(null)} {...snackbarPlacement('top', snackbarTopOffset)}>
        <Alert severity={snapToast?.sev ?? 'success'} variant="filled" onClose={() => setSnapToast(null)}>
          {snapToast?.msg}
        </Alert>
      </Snackbar>
    </AgentChatProvider>
  )
}

/**
 * The full-screen preview, showing the chat's live row for the artifact it was
 * opened on. A web app registered again gets a new updatedAt, and so a new
 * frame URL, only through the chat's artifacts (Matt, 29 Oct 2026). Lives
 * inside the provider to read them.
 */
function LiveArtifactPreviewDialog(props: React.ComponentProps<typeof ArtifactPreviewDialog>) {
  const { artifacts } = useAgentChat()
  const opened = props.artifact
  const live = opened && (artifacts.find(a => a.filePath === opened.filePath) ?? opened)
  return <ArtifactPreviewDialog {...props} artifact={live} />
}

/**
 * The table popup a numbered citation opens (#1296). Lives inside the provider
 * so it can read the session's job from the chat context, the same source the
 * Tables tab uses; the page itself sits outside the provider.
 */
function AgentCitationModal({ customer, citation, onClose }: {
  customer: string
  citation: { table: string; cell?: CitationCell } | null
  onClose: () => void
}) {
  const { session } = useAgentChat()
  return (
    <TableCitationModal
      open={citation !== null}
      onClose={onClose}
      tableName={citation?.table || ''}
      customer={customer}
      job={session?.job || ''}
      highlightCell={citation?.cell}
    />
  )
}

